import type { Asset } from './assets.js';
import { simpleAmount, type SimpleBook } from './simpleCap.js';

/**
 * The Simple ticket's arithmetic, shared by the two places it is shown: the
 * welcome, before any wallet, and the trading screen. Both answer from the
 * same book with the same sums, so what a newcomer is told before making a
 * wallet is what they see after. Pure, so it can be tested.
 */

/**
 * Gaju held back when "use all" fills an offer: the escrow call's fee comes
 * from the seller's own Gaju (at most 0.002 a call, and a first offer may add a
 * registration call). The rest, 0.01 Gaju in all, stays in the wallet.
 */
export const GAJU_FEE_RESERVE = 10n ** 16n;

export type Side = 'buy' | 'sell';

/** What a ticket needs to know of a pair: its units, the fee on each leg, the order charge. */
export interface TicketPair {
  readonly name: string;
  readonly base: string;
  readonly quote: string;
  readonly priceUnit: string;
  /** The contracts' fee on each leg received, in basis points. */
  readonly feeBps: number;
  /** xchange's flat charge on a Solana order, in the asset deposited; null or absent when none. */
  readonly orderFee?: { readonly base: string | null; readonly quote: string | null };
  /** False while a pair cannot take orders yet; absent means it can. */
  readonly live?: boolean;
}

/** A Simple order: what is wanted, cut to what is there, at the best price by someone else. */
export function simpleOrder(
  want: bigint | null,
  book: SimpleBook | null,
): { amount: bigint; price: bigint; capped: boolean } {
  if (want === null) return { amount: 0n, price: book?.price ?? 0n, capped: false };
  const s = simpleAmount(want, book);
  return { amount: s.amount, price: book?.price ?? 0n, capped: s.capped && book !== null };
}

export interface Estimate {
  /** The base coin, in its smallest units. */
  readonly b: bigint;
  /** The price, in the pair's stored unit. */
  readonly p: bigint;
  /** The quote coin the base comes to at that price, before the fee. */
  readonly q: bigint;
  /** What the order spends: the base for a sell, the quote for a bid. */
  readonly spend: Asset;
  /** What the wallet holds of it; null with no wallet. */
  readonly held: bigint | null;
  /** The flat order charge, in the asset spent. */
  readonly fee: bigint;
  /** All the order takes from the wallet: the amount, the charge, and Gaju kept for the network. */
  readonly need: bigint;
  /** The wallet holds less than the order needs. Never true with no wallet. */
  readonly short: boolean;
}

/** What an order of `base` at `price` comes to, and whether the wallet can pay for it. */
export function estimate({
  pair,
  side,
  base,
  price,
  balances,
}: {
  pair: TicketPair;
  side: Side;
  base: bigint;
  price: bigint;
  /** The wallet's balances, or null before any wallet exists. */
  balances: Partial<Record<Asset, string | null>> | null;
}): Estimate | null {
  if (base <= 0n || price <= 0n) return null;
  const q = (base * price) / BigInt(pair.priceUnit);
  const spend = (side === 'sell' ? pair.base : pair.quote) as Asset;
  const held = balances === null ? null : BigInt(balances[spend] ?? '0');
  const reserve = spend === 'GAJU' ? GAJU_FEE_RESERVE : 0n;
  const fee = BigInt((side === 'sell' ? pair.orderFee?.base : pair.orderFee?.quote) ?? '0');
  const need = (side === 'sell' ? base : q) + reserve + fee;
  return { b: base, p: price, q, spend, held, fee, need, short: held !== null && need > held };
}

/**
 * The ticket a newcomer filled in before they had a wallet: the pair, the
 * side and the amount as typed, kept on this device through the PIN and the
 * twelve words, so the trading screen opens with it filled in. Never a
 * price: the ticket answers at the best on the book when it opens.
 */
export interface Draft {
  readonly pair: string;
  readonly side: Side;
  readonly amount: string;
  /** When it was kept, in milliseconds. */
  readonly at: number;
}

const DRAFT_KEY = 'xchange.draft';
/** A week: long enough to buy USDC with a card and come back, short enough not to surprise. */
export const DRAFT_LIFE_MS = 7 * 24 * 60 * 60 * 1000;

export function saveDraft(d: Omit<Draft, 'at'>, now: number = Date.now()): void {
  try {
    if (d.amount.trim() === '') localStorage.removeItem(DRAFT_KEY);
    else localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...d, at: now }));
  } catch {
    /* a private window: the ticket simply opens empty */
  }
}

export function readDraft(now: number = Date.now()): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (raw === null) return null;
    const d = JSON.parse(raw) as Partial<Draft>;
    if (
      typeof d.pair !== 'string' ||
      (d.side !== 'buy' && d.side !== 'sell') ||
      typeof d.amount !== 'string' ||
      d.amount.trim() === '' ||
      typeof d.at !== 'number' ||
      now - d.at > DRAFT_LIFE_MS
    ) {
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return { pair: d.pair, side: d.side, amount: d.amount, at: d.at };
  } catch {
    return null;
  }
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* fine */
  }
}
